import { useCallback, useMemo, useState } from 'react'

import { GiphyControllerService } from '@api'
import debounce from 'lodash/debounce'
import { useInfiniteQuery } from 'react-query'

import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

const GIFS_LIMIT = 10

export const useInfiniteGifsData = () => {
  const [query, setQuery] = useState('')

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
    useInfiniteQuery(
      ['gifs', query],
      ({ pageParam = 0 }) => {
        if (query) {
          return GiphyControllerService.searchGiphy({
            query,
            limit: GIFS_LIMIT,
            offset: pageParam,
          })
        }

        return GiphyControllerService.trendingGiphy({
          limit: GIFS_LIMIT,
          offset: pageParam,
        })
      },
      {
        getNextPageParam: (lastPage) => {
          if (!lastPage.pagination) {
            return undefined
          }
          const allRequested =
            lastPage.pagination.offset + lastPage.pagination.count

          if (allRequested < lastPage.pagination.total_count) {
            return allRequested
          }
        },
      },
    )

  const canLoadMore = Boolean(hasNextPage && !isFetchingNextPage)

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollWidth, scrollLeft, clientWidth } = containerRefElement

        if (scrollWidth - scrollLeft - clientWidth < 100 && canLoadMore) {
          fetchNextPage()
        }
      }
    },
    [canLoadMore, fetchNextPage],
  )

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    fetchMoreOnBottomReached(event.target as HTMLDivElement)
  }

  const handleSearchChange: React.ChangeEventHandler<HTMLInputElement> =
    debounce((event) => {
      setQuery(event.target.value)
    }, 500)

  const list = useMemo(
    () =>
      data?.pages.flatMap((page) => page.data).filter(isTruthyAndNotEmpty) ??
      [],
    [data],
  )

  return {
    query,
    list,
    isLoading,
    handleScroll,
    handleSearchChange,
    isFetchingNextPage,
  }
}
